import React, { useMemo } from 'react';
import { Row, Col, Card, Statistic, Button } from 'antd';
import {
  WarningOutlined,
  BankOutlined,
  ShoppingOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { UnifiedDashboardActionSummary } from '@/services/unifiedDashboard.service';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

export interface ExecutivePriorityStripProps {
  unified: UnifiedDashboardActionSummary;
}

export const ExecutivePriorityStrip: React.FC<ExecutivePriorityStripProps> = ({ unified }) => {
  const navigate = useNavigate();
  const fin = unified.finance;
  const scm = unified.supply_chain;
  const sales = unified.sales;

  const metrics = useMemo(() => {
    const overdueAr = fin.overdue_customer_invoice_count ?? fin.overdue_customer_invoices.length;
    const overdueAp = fin.overdue_supplier_invoice_count ?? fin.overdue_supplier_invoices.length;
    const overduePo = scm.overdue_po_count ?? scm.overdue_pos.length;
    const pendingScmApprovals =
      scm.pending_approval_prs.length +
      scm.pending_approval_bid_analyses.length +
      scm.pending_approval_pos.length +
      scm.pending_approval_grns.length +
      scm.pending_approval_returns.length;
    const pendingFinApprovals =
      fin.pending_approval_payment_vouchers.length +
      fin.pending_approval_journal_vouchers.length +
      fin.pending_approval_grns.length;
    const financeDrafts =
      fin.draft_receipt_vouchers_count +
      fin.draft_payment_vouchers_count +
      fin.draft_journal_vouchers_count;
    const salesAttention =
      sales.pending_review_sales_orders.length + sales.overdue_customer_invoices.length;

    return {
      overdueAr,
      overdueAp,
      overduePo,
      pendingScmApprovals,
      pendingFinApprovals,
      financeDrafts,
      salesAttention,
    };
  }, [fin, scm, sales]);

  const cardStyle: React.CSSProperties = {
    height: '100%',
    borderRadius: 8,
    border: EnterpriseTokens.workspace.borderSubtle,
  };

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic
            title="Overdue receivables"
            value={metrics.overdueAr}
            prefix={<WarningOutlined style={{ color: '#cf1322' }} />}
            valueStyle={{ color: metrics.overdueAr > 0 ? '#cf1322' : undefined }}
          />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/unified')}>
            Open workboard
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic
            title="Overdue payables / POs"
            value={metrics.overdueAp + metrics.overduePo}
            prefix={<BankOutlined style={{ color: '#d46b08' }} />}
          />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/unified')}>
            Review exceptions
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic
            title="Pending approvals (SCM + Finance)"
            value={metrics.pendingScmApprovals + metrics.pendingFinApprovals}
            prefix={<FileTextOutlined style={{ color: '#0958d9' }} />}
          />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/unified')}>
            View queues
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic title="Finance drafts (RV / PV / JV)" value={metrics.financeDrafts} prefix={<BankOutlined />} />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/fin/dashboard')}>
            Finance dashboard
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic
            title="Sales attention (pending review + overdue)"
            value={metrics.salesAttention}
            prefix={<ShoppingOutlined />}
          />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/growth/dashboard')}>
            Sales dashboard
          </Button>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <Card size="small" style={cardStyle} styles={{ body: { padding: 16 } }}>
          <Statistic title="SCM drafts (PR / PO / GRN)" value={scm.draft_pr_count + scm.draft_po_count + scm.draft_grn_count} />
          <Button type="link" size="small" style={{ paddingLeft: 0, marginTop: 8 }} onClick={() => navigate('/scm/dashboard')}>
            SCM dashboard
          </Button>
        </Card>
      </Col>
    </Row>
  );
};
